import type { FormEvent, ReactNode } from 'react';

import type { SignupDraft } from './draft';

/** What every question screen receives from the wizard. */
export type StepProps = {
  draft: SignupDraft;
  update: (patch: Partial<SignupDraft>) => void;
  /** Validate this step and move on. */
  next: () => void;
  /** Save a choice and move on after a beat, so the pick is seen landing. */
  choose: (patch: Partial<SignupDraft>) => void;
  error?: string;
  /** Bumps on every refused attempt, so the error replays its nudge. */
  attempt: number;
};

type QuestionProps = {
  id: string;
  title: ReactNode;
  hint?: string;
  error?: string;
  attempt: number;
  onSubmit: () => void;
  submitLabel?: string;
  busy?: boolean;
  /** Optional steps offer a way past them instead of the Enter hint. */
  onSkip?: () => void;
  /** A short line above the question, for the first screen of a signup. */
  kicker?: ReactNode;
  /** Under the answer, before the way forward: a quiet aside such as the other signup. */
  aside?: ReactNode;
  children: ReactNode;
};

/**
 * One question, one screen: a big serif question, a quiet line on why it is
 * asked, the answer, and one way forward. Enter always moves on.
 */
export function Question({ id, title, hint, error, attempt, onSubmit, submitLabel = 'Seguir', busy, onSkip, kicker, aside, children }: QuestionProps) {
  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    onSubmit();
  }
  return (
    <form className="wiz-q" noValidate onSubmit={handleSubmit}>
      {kicker ? <div className="wiz-kicker">{kicker}</div> : null}
      <h1 className="wiz-title" id={`${id}-title`}>
        {title}
      </h1>
      {hint ? <p className="wiz-hint">{hint}</p> : null}
      <div className="wiz-field">{children}</div>
      {error ? (
        <p className="wiz-error" role="alert" key={attempt}>
          {error}
        </p>
      ) : null}
      <div className="wiz-actions">
        <button type="submit" className="btn wiz-next" disabled={busy}>
          {submitLabel}
        </button>
        {onSkip ? (
          <button type="button" className="wiz-skip" onClick={onSkip}>
            Saltar por ahora
          </button>
        ) : (
          <span className="wiz-enter" aria-hidden="true">
            o presiona <kbd>Enter</kbd>
          </span>
        )}
      </div>
      {aside ? <div className="wiz-aside">{aside}</div> : null}
    </form>
  );
}

/** The business name inside a question, set in the serif italic so it reads as theirs. */
export function Named({ children }: { children: ReactNode }) {
  return <em className="wiz-named">{children}</em>;
}
